import i18n, { type AppLanguageCode, type TKey, translateApp } from '@repo/i18n'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

/** Backend error bodies are thrown directly by the SDK; `code` is top-level. */
export function getErrorCode(error: unknown) {
	return typeof error === 'object' &&
		error !== null &&
		'code' in error &&
		typeof error.code === 'string'
		? error.code
		: undefined
}

/**
 * The message of a refused request in the words of the shared errors. A
 * refusal that names characters the documents cannot print gets them in
 * `{{characters}}`, each as `describeCharacter` shows it.
 */
export function formatError(error: unknown, language?: AppLanguageCode) {
	const code = getErrorCode(error)
	const key = `shared:http_errors.${code?.toLowerCase()}` as TKey
	const resolvedKey =
		code && i18n.exists(key) ? key : ('shared:http_errors.default' as TKey)
	const options = { characters: charactersOf(error) }

	return language
		? translateApp(language, resolvedKey, options)
		: i18n.t(resolvedKey, options)
}

const CharactersAnswerSchema = z.object({
	characters: z.array(z.string()).optional(),
	fields: z.array(z.object({ characters: z.array(z.string()) })).optional()
})

const INVISIBLE_CHARACTER = /^[\p{Cc}\p{Cf}\p{Mn}\p{Me}]$/u

/** A character as a message shows it: its code point, then the character itself, as `U+2713 ✓`. */
export function describeCharacter(char: string) {
	const code = (char.codePointAt(0) ?? 0)
		.toString(16)
		.toUpperCase()
		.padStart(4, '0')
	// A control, format or combining character shows only its code: drawn, it would hide or bend the message.
	return INVISIBLE_CHARACTER.test(char) ? `U+${code}` : `U+${code} ${char}`
}

/** The characters a refusal names, at its top or per field, each described; empty when it names none. */
function charactersOf(error: unknown) {
	const answer = CharactersAnswerSchema.safeParse(error)
	if (!answer.success) {
		return ''
	}
	const { characters = [], fields = [] } = answer.data
	return [
		...new Set([
			...characters,
			...fields.flatMap((field) => field.characters)
		])
	]
		.map(describeCharacter)
		.join(', ')
}

export function translateValidationIssue<Issue extends { code: string }>(
	issue: Issue,
	messages: Partial<Record<string, string | ((issue: Issue) => string)>>,
	fallback: string | ((issue: Issue) => string)
) {
	const message = messages[issue.code] ?? fallback

	if (typeof message === 'function') {
		return message(issue)
	}

	return message
}

/**
 * Function that throws an error, it's useful to prevent impossible states in the application.
 * Example: const id = params.id ?? raise('No id provided'), in this case id is a string instead of string | undefined
 * @param error
 */
export const raise = (error: string): never => {
	throw new Error(error)
}

export function debounce<Args extends unknown[]>(
	callback: (...args: Args) => unknown,
	delay: number
) {
	let timeout: Parameters<typeof clearTimeout>[0] | undefined

	return (...args: Args) => {
		if (timeout !== undefined) {
			clearTimeout(timeout)
		}
		timeout = setTimeout(() => {
			callback(...args)
		}, delay)
	}
}

export function getInitial(value: string) {
	return value.trim().charAt(0).toUpperCase() || '?'
}

export function getInitials(value: string) {
	return value
		.split(' ')
		.map((part) => part[0] ?? '')
		.join('')
		.slice(0, 2)
		.toUpperCase()
}

/** Formats a millisecond duration as `m:ss` (clamped at zero); used by media players and recorders. */
export function formatMediaDuration(durationMs: number) {
	const total = Math.max(0, Math.round(durationMs / 1000))
	return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`
}

/** A size as a person reads it, in the number format of `language`: "10 MB", "1,5 MB". The number and the unit are held together by a no-break space. */
export function formatBytes(bytes: number, language?: string) {
	const units = ['B', 'KB', 'MB', 'GB'] as const
	let value = bytes
	let unit = 0
	while (value >= 1024 && unit < units.length - 1) {
		value /= 1024
		unit++
	}
	// Whole bytes and kilobytes; one decimal from the megabyte up, dropped when it is zero.
	const number = new Intl.NumberFormat(language, {
		maximumFractionDigits: unit >= 2 ? 1 : 0
	}).format(value)
	return `${number}\u00A0${units[unit]}`
}

/** Max items of a generated array schema, or null if unconstrained. */
export function zodArrayMaxLength(schema: z.ZodType) {
	return zodMaxConstraint(schema, 'maxItems')
}

/** Max value of a generated number schema, or null if unconstrained. */
export function zodNumberMaxValue(schema: z.ZodType) {
	return zodMaxConstraint(schema, 'maximum')
}

/** Max length of a generated string schema, or null if unconstrained. */
export function zodStringMaxLength(schema: z.ZodType) {
	return zodMaxConstraint(schema, 'maxLength')
}

/** Min length of a generated string schema, or null if unconstrained. */
export function zodStringMinLength(schema: z.ZodType) {
	return zodMaxConstraint(schema, 'minLength')
}

/**
 * Reads a constraint through any optional/default/nullable wrapping via the
 * public API — zod internals (`_zod`) change between releases and generated
 * wrapper nesting changes between SDK releases.
 */
function zodMaxConstraint(
	schema: z.ZodType,
	key: 'maxItems' | 'maximum' | 'maxLength' | 'minLength'
) {
	let current: z.core.$ZodType = schema
	// Wrapper types only — a general unwrap() loop would also unwrap arrays
	// into their element schema, losing the array's own constraints.
	while (
		current instanceof z.ZodOptional ||
		current instanceof z.ZodDefault ||
		current instanceof z.ZodNullable
	) {
		current = current.unwrap()
	}
	const value = z.toJSONSchema(current)[key]
	return typeof value === 'number' ? value : null
}
